<script setup lang="ts">
/**
 * Full-screen mobile overlay for a single business service occurrence.
 *
 * Rendered as a fixed-position layer (z-index 10, above MobileBottomNav's 5)
 * via Teleport, mirroring `ServiceTypeMobileScreen`'s navbar/scroll structure.
 * Content mirrors `BusinessServiceCard` (desktop): service type + duration,
 * date/time, optional description, performers split by gender, and attending
 * clients — but clients are rendered as one filterable roster list (All/Men/
 * Women segmented control) instead of two side-by-side columns, matching
 * docs/sketches/mobile/business-service-card.html.
 *
 * Edit opens AddServiceFormMobileScreen via useAddBusinessServiceFormStore.
 * The form must be mounted above this screen (z-index >= 11) by the parent
 * to overlay it cleanly without dismissing the card first.
 */
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  deleteBusinessService,
  fetchBusinessService,
  type BusinessService,
} from '@/api/businessServices'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import {
  fetchBusinessServiceTypes,
  type BusinessServiceTypesGroup,
} from '@/api/businessServiceTypes'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { fetchClientsByIds, type Client } from '@/api/clients'
import { fetchPerformers, type Performer } from '@/api/performers'
import {
  businessServicesPollingInterval,
  performersPollingInterval,
  serviceCategoriesPollingInterval,
  serviceTypesPollingInterval,
} from '@/config/api'
import { formatDateTime } from '@/utils/formatDateTime'
import { formatDuration } from '@/utils/formatDuration'
import { compactSince, formatStartDate } from '@/utils/formatStartDate'
import { groupAgeShort } from '@/utils/groupAge'
import { fetchGroup } from '@/api/groups'
import { useGroupDisplay } from '@/composables/useGroupDisplay'
import { genderBucket, type GenderBucket } from '@/utils/gender'
import { useAddBusinessServiceFormStore } from '@/stores/add_business_service_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  /** Business service id (UUID7) selected in the services table. */
  businessServiceId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const businessServiceId = toRef(props, 'businessServiceId')
const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.BusinessServicesEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.BusinessServicesDelete))

const isEnabled = computed(
  () => typeof businessServiceId.value === 'string' && businessServiceId.value.length > 0,
)

// ── Queries ───────────────────────────────────────────────────────────────────
const serviceQuery = useQuery({
  queryKey: ['businessService', businessServiceId],
  queryFn: () => fetchBusinessService(businessServiceId.value),
  enabled: isEnabled,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServicesPollingInterval,
  refetchIntervalInBackground: false,
})

const performersQuery = useInfiniteQuery({
  queryKey: ['performers'],
  queryFn: ({ pageParam }) => fetchPerformers(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: performersPollingInterval,
  refetchInterval: performersPollingInterval,
  refetchIntervalInBackground: false,
})

const serviceTypeGroupsQuery = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceTypesPollingInterval,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
})

const serviceNamesQuery = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: serviceCategoriesPollingInterval,
  refetchInterval: serviceCategoriesPollingInterval,
  refetchIntervalInBackground: false,
})

watch(
  () => ({
    hasNext: performersQuery.hasNextPage.value === true,
    fetching: performersQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void performersQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => ({
    hasNext: serviceTypeGroupsQuery.hasNextPage.value === true,
    fetching: serviceTypeGroupsQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void serviceTypeGroupsQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => ({
    hasNext: serviceNamesQuery.hasNextPage.value === true,
    fetching: serviceNamesQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void serviceNamesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

const service = computed<BusinessService | null>(() => serviceQuery.data.value ?? null)

const clientIds = computed<string[]>(() => {
  const ids = service.value?.client_ids ?? []
  return Array.from(new Set(ids)).sort()
})

const clientsQuery = useQuery({
  queryKey: ['clientsByIds', clientIds],
  queryFn: () => fetchClientsByIds(clientIds.value),
  enabled: computed(() => clientIds.value.length > 0),
  staleTime: 0,
  refetchInterval: businessServicesPollingInterval,
  refetchIntervalInBackground: false,
})

// ── Type label / duration ────────────────────────────────────────────────────
interface ServiceTypeDisplay {
  name: string
  duration: string | null
}

const serviceTypeDisplayById = computed<Map<string, ServiceTypeDisplay>>(() => {
  const categoryNameById = new Map<string, string>()
  for (const page of serviceNamesQuery.data.value?.pages ?? []) {
    for (const item of page.items ?? []) {
      categoryNameById.set(item.id, item.name)
    }
  }

  const out = new Map<string, ServiceTypeDisplay>()
  const groups: BusinessServiceTypesGroup[] = (
    serviceTypeGroupsQuery.data.value?.pages ?? []
  ).flatMap((page) => page.items ?? [])
  for (const group of groups) {
    for (const t of group.business_service_types ?? []) {
      const desc = typeof t.description === 'string' ? t.description.trim() : ''
      const categoryName = categoryNameById.get(group.business_service_name_id)
      const name = desc.length > 0 ? desc : categoryName
      if (!name) continue
      out.set(t.id, { name, duration: formatDuration(t.duration) ?? null })
    }
  }
  return out
})

const serviceTypeDisplay = computed<ServiceTypeDisplay | null>(() => {
  const current = service.value
  if (!current) return null
  return serviceTypeDisplayById.value.get(current.business_service_type_id) ?? null
})

const serviceTypeLabel = computed<string>(() => {
  const current = service.value
  if (!current) return ''
  return (
    serviceTypeDisplay.value?.name ??
    t('tabs.services.serviceTypeFallback', { id: current.business_service_type_id })
  )
})

// The session's group (name + age under the type), fetched by id. Enrichment
// only: needs GroupsView, otherwise the line is simply not shown.
const groupId = computed(() => service.value?.group_id ?? null)
const groupQuery = useQuery({
  queryKey: ['group', groupId],
  queryFn: () => fetchGroup(groupId.value!),
  enabled: computed(() => groupId.value != null && hasRole(ResourceAccessRole.GroupsView)),
})
const { displayName: groupDisplayName } = useGroupDisplay()
const groupLabel = computed<{ name: string; age: string } | null>(() => {
  const g = groupQuery.data.value
  if (!g) return null
  const { duration } = formatStartDate({ date: g.start, timeZone: 'UTC' })
  return { name: groupDisplayName(g), age: groupAgeShort(duration.years, duration.months, t) }
})

const durationLabel = computed<string | null>(() => serviceTypeDisplay.value?.duration ?? null)

const datetimeLabel = computed<{ date: string; time: string }>(() => {
  const raw = service.value?.date_time
  if (!raw) return { date: '-', time: '-' }
  const { formattedDate, formattedTime } = formatDateTime({ date: raw, dateStyle: 'medium' })
  return { date: formattedDate, time: formattedTime }
})

const descriptionLabel = computed<string | null>(() => {
  const raw = service.value?.description
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
})

function displayName(raw: string | null | undefined, fallback: string): string {
  const trimmed = raw?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : fallback
}

// ── Performers (split by gender, mirrors desktop) ────────────────────────────
const performerById = computed<Map<string, Performer>>(() => {
  const map = new Map<string, Performer>()
  for (const page of performersQuery.data.value?.pages ?? []) {
    for (const p of page.items ?? []) map.set(p.id, p)
  }
  return map
})

interface PersonDisplay {
  id: string
  name: string
  gender: GenderBucket
}

const performers = computed<Record<GenderBucket, PersonDisplay[]>>(() => {
  const out: Record<GenderBucket, PersonDisplay[]> = { men: [], women: [] }
  for (const id of service.value?.performers_ids ?? []) {
    const performer = performerById.value.get(id)
    const gender = genderBucket(performer?.gender) ?? 'men'
    out[gender].push({
      id,
      name: displayName(performer?.name, t('tabs.services.performerFallback', { id })),
      gender,
    })
  }
  return out
})

// ── Clients (unified roster, filterable by gender) ───────────────────────────
const clientById = computed<Map<string, Client>>(() => {
  const map = new Map<string, Client>()
  for (const c of clientsQuery.data.value?.items ?? []) {
    map.set(c.id, c)
  }
  return map
})

interface RosterEntry {
  id: string
  name: string
  gender: GenderBucket
  sinceLabel: string | null
  lessonsLabel: string | null
}

/**
 * Interleaves men/women one-by-one (matches the sketch's alternating roster);
 * once one side runs out, the remaining entries of the other side follow in
 * their original order.
 */
function interleaveByGender(entries: RosterEntry[]): RosterEntry[] {
  const men = entries.filter((e) => e.gender === 'men')
  const women = entries.filter((e) => e.gender === 'women')
  const out: RosterEntry[] = []
  const max = Math.max(men.length, women.length)
  for (let i = 0; i < max; i++) {
    const man = men[i]
    const woman = women[i]
    if (man) out.push(man)
    if (woman) out.push(woman)
  }
  return out
}

const roster = computed<RosterEntry[]>(() => {
  const ids = service.value?.client_ids ?? []
  const entries = ids.map((id) => {
    const client = clientById.value.get(id)
    const gender = genderBucket(client?.gender) ?? 'men'
    const rawCount = client?.business_service_count
    const count = typeof rawCount === 'number' && Number.isFinite(rawCount) ? rawCount : null
    return {
      id,
      name: displayName(client?.name, t('common.personFallback', { id })),
      gender,
      sinceLabel: compactSince(client?.start_datetime),
      lessonsLabel: count !== null ? t('cards.businessService.lessonsLabel', count) : null,
    }
  })
  return interleaveByGender(entries)
})

const clientCount = computed<number>(() => service.value?.client_ids.length ?? 0)

const genderCounts = computed<{ men: number; women: number }>(() => {
  let men = 0
  let women = 0
  for (const entry of roster.value) {
    if (entry.gender === 'women') women++
    else men++
  }
  return { men, women }
})

type RosterFilter = 'all' | 'men' | 'women'
const filter = ref<RosterFilter>('all')

const filteredRoster = computed<RosterEntry[]>(() => {
  if (filter.value === 'all') return roster.value
  return roster.value.filter((entry) => entry.gender === filter.value)
})

// ── Edit ──────────────────────────────────────────────────────────────────────
const addServiceFormStore = useAddBusinessServiceFormStore()

function openEdit(): void {
  const s = service.value
  if (!s) return
  // 'businessService', not 'addBusinessServiceForm': clearInfoViewSelections
  // clears every OTHER store, so exempting the form would wipe this
  // screen's own businessServiceCardStore selection — the same selection
  // its `v-if` in ServicesTab depends on. Losing it here means the user
  // drops back to the plain services list (not this screen) the moment
  // the edit form closes.
  clearInfoViewSelections('businessService')
  addServiceFormStore.openForEdit(s.id, {
    serviceTypeId: s.business_service_type_id,
    dateTime: s.date_time,
    performerIds: s.performers_ids,
    clientIds: s.client_ids,
    groupId: s.group_id ?? null,
    description: s.description ?? null,
    price: s.price,
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteBusinessService(businessServiceId.value),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['businessServices'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() =>
  t('cards.businessService.confirmDeleteDescription', {
    type: serviceTypeLabel.value,
    date: datetimeLabel.value.date,
    count: clientCount.value,
  }),
)

function handleDelete(): void {
  if (!service.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="svm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="svm-navbar">
        <button type="button" class="svm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('nav.services') }}
        </button>
        <span class="svm-nav-title">{{ t('cards.businessService.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="svm-nav-edit"
          :disabled="!service"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <!-- Loading / error -->
      <div v-if="serviceQuery.isPending.value && !service" class="svm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div v-else-if="serviceQuery.isError.value && !service" class="svm-state svm-state--error">
        {{ t('cards.businessService.loadErrorShort') }}
      </div>

      <!-- Content -->
      <div v-else-if="service" class="svm-scroll">
        <div class="svm-header">
          <div class="svm-header-left">
            <div class="svm-type">{{ serviceTypeLabel }}</div>
            <div v-if="durationLabel" class="svm-duration">{{ durationLabel }}</div>
            <div v-if="groupLabel" class="svm-group">
              <span class="svm-group-name">{{ groupLabel.name }}</span>
              <span class="svm-group-age">· {{ groupLabel.age }}</span>
            </div>
          </div>
          <div class="svm-balance">
            <span class="svm-balance-men">{{ genderCounts.men }}{{ t('common.maleAbbr') }}</span
            ><span class="svm-balance-sep">/</span
            ><span class="svm-balance-women"
              >{{ genderCounts.women }}{{ t('common.femaleAbbr') }}</span
            >
          </div>
        </div>

        <div class="svm-when">
          <span class="svm-date">{{ datetimeLabel.date }}</span>
          <span class="svm-time">{{ datetimeLabel.time }}</span>
        </div>

        <div v-if="descriptionLabel" class="svm-desc">{{ descriptionLabel }}</div>

        <div class="svm-perf-cap">Performers</div>
        <div class="svm-perf-grid">
          <div class="svm-perf-col">
            <span
              v-for="p in performers.men"
              :key="p.id"
              class="svm-perf-name svm-perf-name--men"
              >{{ p.name }}</span
            >
            <span v-if="performers.men.length === 0" class="svm-perf-empty">–</span>
          </div>
          <div class="svm-perf-col">
            <span
              v-for="p in performers.women"
              :key="p.id"
              class="svm-perf-name svm-perf-name--women"
              >{{ p.name }}</span
            >
            <span v-if="performers.women.length === 0" class="svm-perf-empty">–</span>
          </div>
        </div>

        <div class="svm-section-label">
          <span>{{ t('tabs.services.colClients') }}</span>
          <span>{{ t('cards.businessService.registered', { count: clientCount }) }}</span>
        </div>

        <div class="svm-seg">
          <button
            type="button"
            :class="{ 'svm-seg-btn--active': filter === 'all' }"
            @click="filter = 'all'"
          >
            {{ t('cards.businessService.allFilter', { count: clientCount }) }}
          </button>
          <button
            type="button"
            :class="{ 'svm-seg-btn--active': filter === 'men' }"
            @click="filter = 'men'"
          >
            <span class="svm-dot svm-dot--men" aria-hidden="true" />{{ genderCounts.men }}
          </button>
          <button
            type="button"
            :class="{ 'svm-seg-btn--active': filter === 'women' }"
            @click="filter = 'women'"
          >
            <span class="svm-dot svm-dot--women" aria-hidden="true" />{{ genderCounts.women }}
          </button>
        </div>

        <div class="svm-roster">
          <div v-if="filteredRoster.length === 0" class="svm-roster-empty">
            {{ t('cards.businessService.noClients') }}
          </div>
          <div v-for="entry in filteredRoster" :key="entry.id" class="svm-roster-row">
            <div
              class="svm-roster-tick"
              :class="entry.gender === 'women' ? 'svm-roster-tick--women' : 'svm-roster-tick--men'"
            />
            <div class="svm-roster-main">
              <div
                class="svm-roster-name"
                :class="
                  entry.gender === 'women' ? 'svm-roster-name--women' : 'svm-roster-name--men'
                "
              >
                {{ entry.name }}
              </div>
              <div v-if="entry.sinceLabel || entry.lessonsLabel" class="svm-roster-sub">
                <template v-if="entry.sinceLabel">{{ entry.sinceLabel }}</template
                ><template v-if="entry.sinceLabel && entry.lessonsLabel"> · </template
                ><template v-if="entry.lessonsLabel">{{ entry.lessonsLabel }}</template>
              </div>
            </div>
          </div>
        </div>

        <!-- Grows to push Delete to the bottom of the screen when content is
             short; shrinks to 0 and lets the normal scroll take over once
             content overflows the viewport. -->
        <div class="svm-fill" />

        <!-- Delete -->
        <div v-if="canDelete" class="svm-danger-group">
          <button
            type="button"
            class="svm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="svm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.businessService.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.svm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
.svm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.svm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.svm-nav-back:active {
  opacity: 0.6;
}

.svm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.svm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.svm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.svm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

/* ── Loading / error ─────────────────────────────────────────────────────── */
.svm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.svm-state--error {
  color: var(--color-money-negative);
}

/* ── Scrollable content ──────────────────────────────────────────────────── */
.svm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.svm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.125rem 0.85rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.svm-header-left {
  min-width: 0;
  flex: 1;
}

.svm-type {
  /* --text-tufte-lg, matching the size now used consistently everywhere
   * a business service type's own name is shown. */
  font-size: var(--text-tufte-lg);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: 0.01em;
}

.svm-duration {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.3rem;
}

.svm-group {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  margin-top: 0.3rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
}

.svm-group-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.svm-group-age {
  flex: 0 0 auto;
  white-space: nowrap;
  color: var(--color-tufte-muted);
}

.svm-balance {
  flex-shrink: 0;
  font-family: var(--font-tufte-mono);
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
  margin-top: 0.3rem;
}
.svm-balance-men {
  color: var(--color-gender-men);
}
.svm-balance-women {
  color: var(--color-gender-women);
}
.svm-balance-sep {
  color: var(--color-tufte-muted);
  margin: 0 0.15rem;
}

/* ── Date/time ───────────────────────────────────────────────────────────── */
.svm-when {
  padding: 0.6rem 1.125rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  display: flex;
  align-items: baseline;
  gap: 0.8rem;
}

.svm-date,
.svm-time {
  font-family: var(--font-tufte-mono);
  font-size: 0.95rem;
  letter-spacing: 0.03em;
}
.svm-time {
  color: var(--color-tufte-muted);
}

/* ── Description ─────────────────────────────────────────────────────────── */
.svm-desc {
  padding: 0.6rem 1.125rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  font-size: 0.92rem;
  line-height: 1.5;
  color: var(--color-tufte-secondary);
  font-style: italic;
}

/* ── Performers ──────────────────────────────────────────────────────────── */
.svm-perf-cap {
  padding: 0.5rem 0.85rem 0.15rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  background: var(--color-tufte-hover);
}

.svm-perf-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-hover);
}

.svm-perf-col {
  padding: 0.3rem 0.85rem 0.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.svm-perf-name {
  font-size: 0.98rem;
  line-height: 1.3;
  overflow-wrap: break-word;
}
.svm-perf-name--men {
  color: var(--color-gender-men);
}
.svm-perf-name--women {
  color: var(--color-gender-women);
}

.svm-perf-empty {
  font-size: 0.85rem;
  color: var(--color-tufte-muted);
  font-style: italic;
}

/* ── Clients section label ───────────────────────────────────────────────── */
.svm-section-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 1.125rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  font-family: var(--font-tufte-sans);
  font-size: 0.66rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

/* ── Gender segmented control ───────────────────────────────────────────── */
.svm-seg {
  display: flex;
  gap: 0.5rem;
  padding: 0.55rem 1.125rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.svm-seg button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-height: 2rem;
  padding: 0.3rem 0.7rem;
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 999px;
  font: inherit;
  font-size: 0.82rem;
  color: var(--color-tufte-muted);
  cursor: pointer;
  transition:
    background 0.1s,
    color 0.1s,
    border-color 0.1s;
}

.svm-seg-btn--active {
  background: color-mix(in srgb, var(--color-tufte-text) 10%, var(--color-tufte-bg));
  color: var(--color-tufte-text);
  border-color: color-mix(in srgb, var(--color-tufte-text) 40%, transparent);
  font-weight: 700;
}

.svm-dot {
  width: 0.44rem;
  height: 0.44rem;
  border-radius: 50%;
  display: inline-block;
}
.svm-dot--men {
  background: var(--color-gender-men);
}
.svm-dot--women {
  background: var(--color-gender-women);
}

/* ── Roster ──────────────────────────────────────────────────────────────── */
.svm-roster {
  background: var(--color-tufte-bg);
}

.svm-roster-empty {
  padding: 0.75rem 1.125rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.82rem;
  color: var(--color-tufte-muted);
}

.svm-roster-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.5rem 1.125rem;
  border-bottom: 1px solid var(--color-tufte-border-light);
}
.svm-roster-row:last-child {
  border-bottom: none;
}

.svm-roster-tick {
  flex-shrink: 0;
  align-self: stretch;
  width: 3px;
  border-radius: 2px;
}
.svm-roster-tick--men {
  background: var(--color-gender-men);
}
.svm-roster-tick--women {
  background: var(--color-gender-women);
}

.svm-roster-main {
  flex: 1;
  min-width: 0;
}

.svm-roster-name {
  font-size: 0.98rem;
  line-height: 1.2;
  overflow-wrap: break-word;
}
.svm-roster-name--men {
  color: var(--color-gender-men);
}
.svm-roster-name--women {
  color: var(--color-gender-women);
}

.svm-roster-sub {
  font-family: var(--font-tufte-mono);
  font-size: 0.7rem;
  color: var(--color-tufte-muted);
  margin-top: 0.12rem;
}

.svm-fill {
  flex: 1;
}

/* ── Danger group ────────────────────────────────────────────────────────── */
.svm-danger-group {
  margin-top: 1.4rem;
}

.svm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.svm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.svm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.svm-scroll-end {
  height: 2.5rem;
}
</style>
